import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getHappeningNow } from '../api/happeningNow';
import { useAuth } from './AuthContext';
import PlatformIcon from './PlatformIcon';
import TickerSectionMarker from './TickerSectionMarker';
import { resolveTickerChannel, resolveTickerDetail } from '../utils/tickerDisplay';
import {
  countryCodeToFlagEmoji,
  getTickerRegionLabel,
  resolveTickerRegion,
} from '../utils/countryFlag';
import { buildTickerTrackSegments, hasTickerContent } from '../utils/happeningNowFeed';
import { goToSignup } from '../utils/guestSearch';
import '../styles/dynamicFooterTicker.css';

const PlayIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg>
);
const BellIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
    <path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.7 21a2 0 0 1-3.4 0" />
  </svg>
);
const ChevronIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
    <path d="m6 9 6 6 6-6" />
  </svg>
);

/** Target linear scroll speed once loop width is known (px/s). */
const TICKER_PIXELS_PER_SECOND = 88;

const TickerItem = ({ item, section, onPlay, onNotify }) => {
  const platform = item.platform || item.source || 'epg';
  const channel = resolveTickerChannel(item);
  const detail = resolveTickerDetail(item);
  const { scope, countryCode } = resolveTickerRegion(item, section);
  const countryFlag = scope === 'country' ? countryCodeToFlagEmoji(countryCode) : null;
  const countryLabelText = countryFlag ? getTickerRegionLabel(scope, countryCode) : null;
  const showPlay = item.live;
  const showNotify = !item.live;

  return (
    <span className="ticker-item">
      <PlatformIcon platform={platform} className="ti-platform" />

      <span className="ti-text">
        <span className="ti-title" title={item.name}>{item.name}</span>
        {channel && (
          <>
            <span className="ti-sep" aria-hidden="true">·</span>
            <span className="ti-channel">{channel}</span>
          </>
        )}
        {countryFlag && (
          <>
            <span className="ti-sep" aria-hidden="true">·</span>
            <span className="ti-inline-flag" title={countryLabelText} aria-label={countryLabelText}>
              {countryFlag}
            </span>
          </>
        )}
        {detail && (
          <>
            <span className="ti-sep" aria-hidden="true">·</span>
            <span className="ti-detail">{detail}</span>
          </>
        )}
      </span>

      {(showPlay || showNotify) && (
        <span className="ti-actions">
          {showPlay && (
            <button
              type="button"
              className="ti-btn ti-btn-play"
              aria-label={`Watch ${item.name}`}
              onClick={() => onPlay(item)}
            >
              <PlayIcon />
            </button>
          )}
          {showNotify && (
            <button
              type="button"
              className="ti-btn ti-btn-notify"
              aria-label={`Remind me about ${item.name}`}
              onClick={() => onNotify(item)}
            >
              <BellIcon />
            </button>
          )}
        </span>
      )}
    </span>
  );
};

const TickerTrack = ({ segments, onPlay, onNotify }) => (
  <>
    {segments.map(segment => {
      if (segment.type === 'section') {
        return (
          <TickerSectionMarker
            key={segment.key}
            section={segment.section}
          />
        );
      }

      return (
        <TickerItem
          key={segment.key}
          item={segment.item}
          section={segment.section}
          onPlay={onPlay}
          onNotify={onNotify}
        />
      );
    })}
  </>
);

const HIDE_TICKER_PATHS = new Set([
  '/privacy',
  '/terms',
  '/login',
  '/signup',
  '/signup-success',
  '/oauth/success',
  '/oauth/error',
  '/oauth/callback',
  '/ui-lab',
]);

/**
 * DynamicFooterTicker — fixed bottom bar with scoped live feeds.
 * Renders nothing when all sections are empty.
 */
const DynamicFooterTicker = () => {
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sections, setSections] = useState([]);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    let active = true;
    getHappeningNow()
      .then(data => { if (active) setSections(Array.isArray(data) ? data : []); })
      .catch(() => { if (active) setSections([]); });
    return () => { active = false; };
  }, []);

  const segments = useMemo(
    () => buildTickerTrackSegments(sections),
    [sections]
  );

  const loopRef = useRef(null);
  const [scrollMetrics, setScrollMetrics] = useState(null);

  useLayoutEffect(() => {
    const loop = loopRef.current;
    if (!loop || !segments.length) {
      setScrollMetrics(null);
      return undefined;
    }

    const measure = () => {
      const loopWidth = loop.getBoundingClientRect().width;
      if (loopWidth <= 0) {
        return;
      }
      const durationSec = loopWidth / TICKER_PIXELS_PER_SECOND;
      setScrollMetrics({
        distancePx: loopWidth,
        durationSec,
      });
    };

    measure();
    const raf = requestAnimationFrame(measure);

    const observer = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(measure)
      : null;
    observer?.observe(loop);
    window.addEventListener('resize', measure);

    return () => {
      cancelAnimationFrame(raf);
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [segments]);

  const promptSignup = () => {
    goToSignup(navigate);
  };

  const handlePlay = (item) => {
    if (item.url) {
      window.open(item.url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleNotify = () => {
    if (!isLoggedIn) {
      promptSignup();
    }
  };

  if (HIDE_TICKER_PATHS.has(location.pathname)) return null;
  if (!hasTickerContent(sections) || !segments.length) return null;

  const trackStyle = scrollMetrics
    ? {
        '--ticker-scroll-distance': `${scrollMetrics.distancePx}px`,
        '--ticker-scroll-duration': `${scrollMetrics.durationSec}s`,
      }
    : undefined;

  return (
    <>
      <footer className={`ticker${collapsed ? ' collapsed' : ''}`} aria-label="Live events ticker">
        <div className="ticker-label">HAPPENING NOW</div>
        <div className="ticker-viewport">
          <div
            className={`ticker-track${scrollMetrics ? ' ticker-track--ready' : ''}`}
            style={trackStyle}
          >
            <div className="ticker-loop" ref={loopRef}>
              <TickerTrack segments={segments} onPlay={handlePlay} onNotify={handleNotify} />
            </div>
            <div className="ticker-loop" aria-hidden="true">
              <TickerTrack segments={segments} onPlay={handlePlay} onNotify={handleNotify} />
            </div>
          </div>
        </div>
      </footer>
      <button
        type="button"
        className={`ticker-toggle${collapsed ? ' collapsed' : ''}`}
        onClick={() => setCollapsed(c => !c)}
        aria-label={collapsed ? 'Show ticker' : 'Hide ticker'}
      >
        <ChevronIcon />
      </button>
    </>
  );
};

export default DynamicFooterTicker;
